import { useState, useRef, useEffect, useCallback } from "react";
import { createWorker, Worker, PSM } from "tesseract.js";

const CONSIGNMENT_PATTERN = /88\d{11}/;

interface UseTntOcrOptions {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    onScan: (value: string) => void;
    active: boolean;
}

interface UseTntOcrReturn {
    ocrStatus: "idle" | "loading" | "scanning";
    lastRead: string;
}

const useTntOcr = ({ videoRef, onScan, active }: UseTntOcrOptions): UseTntOcrReturn => {
    const [ocrStatus, setOcrStatus] = useState<"idle" | "loading" | "scanning">("idle");
    const [lastRead, setLastRead] = useState("");

    const workerRef = useRef<Worker | null>(null);
    const cooldownRef = useRef(false);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const onScanRef = useRef(onScan);
    const activeRef = useRef(active);
    // Double-match confirmation — requires the same number from two
    // consecutive reads before submitting, to eliminate misreads.
    const lastMatchRef = useRef<string | null>(null);
    useEffect(() => { onScanRef.current = onScan; }, [onScan]);
    useEffect(() => { activeRef.current = active; }, [active]);

    const getCanvas = useCallback(() => {
        if (!canvasRef.current) {
            canvasRef.current = document.createElement("canvas");
        }
        return canvasRef.current;
    }, []);

    // Crop centre of video, downscale, grayscale + contrast stretch.
    const preprocessFrame = useCallback((video: HTMLVideoElement): HTMLCanvasElement | null => {
        if (video.readyState < 2) return null;

        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (!vw || !vh) return null;

        // Capture a reasonable strip around the centre — user aligns the
        // number within the orange target box.
        const cropW = Math.floor(vw * 0.80);
        const cropH = Math.floor(vh * 0.15);
        const cropX = Math.floor((vw - cropW) / 2);
        const cropY = Math.floor((vh - cropH) / 2);

        const outW = Math.floor(cropW * 0.8);
        const outH = Math.floor(cropH * 0.8);

        const canvas = getCanvas();
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, outW, outH);

        const imageData = ctx.getImageData(0, 0, outW, outH);
        const data = imageData.data;

        // First pass — grayscale + min/max
        const gray = new Uint8Array(outW * outH);
        let min = 255, max = 0;
        for (let i = 0, p = 0; i < data.length; i += 4, p++) {
            const g = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
            gray[p] = g;
            if (g < min) min = g;
            if (g > max) max = g;
        }
        const range = max - min || 1;

        // Second pass — contrast stretch to 0-255
        for (let i = 0, p = 0; i < data.length; i += 4, p++) {
            const val = ((gray[p] - min) / range) * 255;
            data[i] = val;
            data[i + 1] = val;
            data[i + 2] = val;
        }
        ctx.putImageData(imageData, 0, 0);

        return canvas;
    }, [getCanvas]);

    // Single OCR tick
    const ocrTick = useCallback(async () => {
        if (cooldownRef.current) return;
        if (!activeRef.current) return;

        const video = videoRef.current;
        const worker = workerRef.current;
        if (!video || !worker) return;

        try {
            const canvas = preprocessFrame(video);
            if (canvas) {
                const t0 = performance.now();
                const result = await worker.recognize(canvas);
                const ms = Math.round(performance.now() - t0);
                const raw = result.data.text.trim();
                const cleaned = raw.replace(/\s/g, "");
                const match = cleaned.match(CONSIGNMENT_PATTERN);

                // Update the live display + console debug
                if (cleaned) {
                    setLastRead(cleaned);
                }
                console.log(`[TNT OCR] ${ms}ms | raw: "${raw}" | cleaned: "${cleaned}" | match: ${match ? match[0] : "none"}`);

                if (match) {
                    const value = match[0];
                    if (value === lastMatchRef.current) {
                        // Same number on two consecutive reads — confirmed
                        onScanRef.current(value);
                        lastMatchRef.current = null;
                        cooldownRef.current = true;
                        setTimeout(() => { cooldownRef.current = false; }, 1000);
                    } else {
                        // First read — remember it, need one more matching read
                        lastMatchRef.current = value;
                    }
                } else {
                    // No match — reset confirmation
                    lastMatchRef.current = null;
                }
            }
        } catch (err) {
            console.warn("[TNT OCR] tick error:", err);
        }
    }, [videoRef, preprocessFrame]);

    useEffect(() => {
        if (active) {
            let cancelled = false;

            const init = async () => {
                setOcrStatus("loading");
                try {
                    const worker = await createWorker("eng");
                    if (cancelled) {
                        await worker.terminate();
                        return;
                    }
                    await worker.setParameters({
                        tessedit_char_whitelist: "0123456789",
                        tessedit_pageseg_mode: PSM.SINGLE_LINE,
                    });
                    workerRef.current = worker;
                    setOcrStatus("scanning");

                    // Process a frame, pause 200ms, repeat. ~5 scans/sec is
                    // plenty for reading a static label without overheating.
                    const loop = async () => {
                        while (!cancelled) {
                            await ocrTick();
                            await new Promise(r => setTimeout(r, 200));
                        }
                    };
                    loop();
                } catch (err) {
                    console.warn("[TNT OCR] Failed to init worker:", err);
                    setOcrStatus("idle");
                }
            };

            init();

            return () => {
                cancelled = true;
                if (intervalRef.current) {
                    clearTimeout(intervalRef.current);
                    intervalRef.current = null;
                }
                if (workerRef.current) {
                    workerRef.current.terminate().catch(() => {});
                    workerRef.current = null;
                }
                setOcrStatus("idle");
                setLastRead("");
                cooldownRef.current = false;
            };
        }
    }, [active, ocrTick]);

    return { ocrStatus, lastRead };
};

export default useTntOcr;
